/**
 * Champs personnalises d'une entree (numero client, PIN, question secrete...). Ils vivent UNIQUEMENT dans les
 * secrets chiffres (`secrets.customFields`, `EntryCustomField` du cœur crypto) : nom ET valeur sont scelles avec
 * l'entree, jamais en metadonnee. `hidden` n'est qu'une indication d'affichage (valeur masquee a l'ecran), la
 * valeur d'un champ visible est chiffree exactement de la meme facon. Module pur, zero E/S.
 */
import type { EntryCustomField, EntrySecrets } from '@klavox/crypto-core';

/** Champs conserves par entree (un import aberrant ne fait pas exploser la fiche). */
export const MAX_CUSTOM_FIELDS = 50;

/** Nom d'un champ dont l'import ne donne aucun nom : la valeur ne doit jamais etre perdue pour autant. */
export const DEFAULT_CUSTOM_FIELD_NAME = 'Champ';

/** Valeur d'une case a cocher : `'true'` (cochee) ou `'false'`, comme les stocke Bitwarden. */
export type CheckboxValue = 'true' | 'false';

/** Case a cocher (`kind: 'boolean'`) : sa valeur est un etat, ni masquee, ni copiee, ni cherchee. */
export function isCheckboxField(field: EntryCustomField): boolean {
  return field.kind === 'boolean';
}

/** Etat d'une case lu d'une valeur importee : seule `true` (casse et espaces ignores) coche, tout le reste decoche. */
export function checkboxValue(value: string): CheckboxValue {
  return value.trim().toLowerCase() === 'true' ? 'true' : 'false';
}

/** Etat d'une case en mots, pour la fiche et l'historique. */
export function checkboxLabel(value: string): string {
  return checkboxValue(value) === 'true' ? 'Cochee' : 'Non cochee';
}

/**
 * Champs normalises : nom rogne (nom par defaut si seule la valeur est remplie), ligne entierement vide retiree,
 * `hidden` ecrit seulement s'il est vrai, plafond `MAX_CUSTOM_FIELDS`. La valeur n'est jamais rognee : un espace
 * peut faire partie d'un secret. Une case a cocher garde son type, sa valeur ramenee a `'true'` / `'false'`, jamais
 * `hidden` ; sans nom elle ne dit rien (son etat existe toujours) et elle est retiree.
 */
export function normalizeCustomFields(fields: readonly EntryCustomField[]): EntryCustomField[] {
  return fields
    .map((field) => ({ name: field.name.trim(), value: isCheckboxField(field) ? checkboxValue(field.value) : field.value, hidden: field.hidden === true, checkbox: isCheckboxField(field) }))
    .filter(({ name, value, checkbox }) => name.length > 0 || (!checkbox && value.length > 0))
    .slice(0, MAX_CUSTOM_FIELDS)
    .map(({ name, value, hidden, checkbox }): EntryCustomField =>
      checkbox
        ? { name, value, kind: 'boolean' }
        : { name: name || DEFAULT_CUSTOM_FIELD_NAME, value, ...(hidden ? { hidden: true } : {}) },
    );
}

/** Champs d'une entree ; les elements mal formes (serveur, import d'une version future) sont ignores. */
export function readCustomFields(secrets: EntrySecrets): EntryCustomField[] {
  const raw: unknown = secrets['customFields'];
  if (!Array.isArray(raw)) return [];
  return normalizeCustomFields(
    raw.filter(
      (item): item is EntryCustomField =>
        item !== null && typeof item === 'object' && typeof item.name === 'string' && typeof item.value === 'string',
    ),
  );
}

/** Lignes montrees d'emblee par la zone de saisie d'une valeur multiligne (au-dela, elle defile ou s'agrandit a la main). */
export const MAX_CUSTOM_VALUE_ROWS = 8;

/**
 * Valeur sur plusieurs lignes (cle, certificat, codes de secours, adresse importes de KeePass, Bitwarden, 1Password) :
 * l'editeur la met dans une zone de texte. Un `<input>` en RETIRE les retours a la ligne (spec HTML, « strip
 * newlines » de la valeur d'un champ texte ou mot de passe) : la fiche rouverte puis enregistree l'aplatirait.
 */
export function isMultilineCustomValue(value: string): boolean {
  return /[\r\n]/.test(value);
}

/** Hauteur de la zone de saisie : une rangee par ligne de la valeur, entre 2 et `MAX_CUSTOM_VALUE_ROWS`. */
export function customValueRows(value: string): number {
  return Math.min(MAX_CUSTOM_VALUE_ROWS, Math.max(2, value.split(/\r\n|\r|\n/).length));
}

/**
 * Saisie dans une valeur d'une ligne qui la fait passer a plusieurs : Maj+Entree (`text` = `\n`) ou texte colle a
 * retours a la ligne, que le champ d'une ligne aplatirait. La selection `[start, end)` est remplacee par `text` ;
 * rend la nouvelle valeur et le curseur juste apres l'insertion. Bornes hors valeur ramenees dedans.
 */
export function spliceCustomValue(value: string, start: number, end: number, text: string): { value: string; caret: number } {
  const from = Math.max(0, Math.min(start, value.length));
  const to = Math.max(from, Math.min(end, value.length));
  return { value: value.slice(0, from) + text + value.slice(to), caret: from + text.length };
}
